import { useState, useEffect } from 'react';
import { BookOpen, Loader2, CheckCircle2, AlertCircle, Save, X, Search, ChevronLeft, ChevronRight, MapPin, Eye, Plus } from 'lucide-react';
import { resolveContractContext } from '../utils/contractResolver';
import { isWordApiAvailable } from '../utils/wordApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface ExtraClause {
  id: string;
  title: string;
  text: string;
  position: number;
}

interface CurrentContract {
  contractId: string;
  tenantId: string;
  templateId: string;
  templateTypeId: string;
  templateTypeName: string;
}

interface TemplateClause {
  id: string;
  clause_head: string;
  clause_content: string;
  clause_type: string;
  isExtra?: boolean;
}

interface ResponsibleUser {
  id: string;
  name: string;
  email: string;
}

interface SaveClauseForm {
  clauseHead: string;
  clauseContent: string;
  clauseType: '' | 'Mandatory' | 'Optional' | 'Conditional';
  templates: string[];
}

export function ClauseLibrary() {
  const [extraClauses, setExtraClauses] = useState<ExtraClause[]>([]);
  const [allClauses, setAllClauses] = useState<TemplateClause[]>([]);
  const [filteredClauses, setFilteredClauses] = useState<TemplateClause[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentContract, setCurrentContract] = useState<CurrentContract | null>(null);
  const [selectedClause, setSelectedClause] = useState<TemplateClause | null>(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [responsibleUsers, setResponsibleUsers] = useState<ResponsibleUser[]>([]);
  const [formData, setFormData] = useState<SaveClauseForm>({
    clauseHead: '',
    clauseContent: '',
    clauseType: 'Optional',
    templates: [],
  });
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [clauseTypeFilter, setClauseTypeFilter] = useState<'all' | 'Mandatory' | 'Optional' | 'Conditional' | 'Extra'>('all');
  const itemsPerPage = 10;

  // Load current contract context (works with both WOPI and SharePoint/Auth modes)
  useEffect(() => {
    resolveContractContext().then((ctx) => {
      if (ctx) setCurrentContract(ctx as CurrentContract);
    }).catch((err) => console.error('Error loading current contract:', err));
  }, []);

  // Load template clauses and extra clauses
  useEffect(() => {
    const loadClauses = async () => {
      // Without tenantId we can't query the library; bail to localstorage-only.
      if (!currentContract?.tenantId) {
        const storedClauses = localStorage.getItem('ezylex-extra-clauses');
        if (storedClauses) {
          try {
            const parsed = JSON.parse(storedClauses);
            setExtraClauses(parsed);
            const extraAsTemplate = parsed.map((ec: ExtraClause) => ({
              id: ec.id,
              clause_head: ec.title,
              clause_content: ec.text,
              clause_type: 'Optional',
              isExtra: true,
            }));
            setAllClauses(extraAsTemplate);
          } catch {
            setAllClauses([]);
          }
        } else {
          setAllClauses([]);
        }
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch template clauses. Server resolves the contract type from
        // contractId/templateId/contractTypeId in that order — and if
        // none resolves, it returns ALL active tenant clauses so the
        // library is at least usable for direct-upload contracts.
        const qs = new URLSearchParams({ tenantId: currentContract.tenantId });
        if (currentContract.contractId) qs.set('contractId', currentContract.contractId);
        if (currentContract.templateId) qs.set('templateId', currentContract.templateId);
        const response = await fetch(
          `${API_BASE_URL}/api/addin/clause-libraries-public?${qs.toString()}`,
          {
            headers: { 
              'ngrok-skip-browser-warning': 'true',
              'x-addin-token': API_TOKEN,
            },
          }
        );
        
        if (!response.ok) {
          console.error('Clause library API error:', response.status);
          setAllClauses([]);
        } else {
          const data = await response.json();
          const clauses = data.clauseLibraries || [];
          
          // Load extra clauses from localStorage
          const storedClauses = localStorage.getItem('ezylex-extra-clauses');
          if (storedClauses) {
            try {
              const parsed = JSON.parse(storedClauses);
              setExtraClauses(parsed);
              
              // Convert extra clauses to template clause format and mark as extra
              const extraAsTemplate = parsed.map((ec: ExtraClause) => ({
                id: ec.id,
                clause_head: ec.title,
                clause_content: ec.text,
                clause_type: 'Optional',
                isExtra: true,
              }));
              
              // Combine template clauses with extra clauses
              setAllClauses([...clauses, ...extraAsTemplate]);
            } catch (err) {
              console.error('Error parsing extra clauses:', err);
              setAllClauses(clauses);
            }
          } else {
            setAllClauses(clauses);
          }
        }
      } catch (err) {
        console.error('Error loading clauses:', err);
      } finally {
        setLoading(false);
      }
    };

    loadClauses();
  }, [currentContract?.templateId, currentContract?.tenantId, currentContract?.contractId]);

  // Filter and paginate clauses
  useEffect(() => {
    let filtered = allClauses;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (clause) =>
          clause.clause_head.toLowerCase().includes(searchQuery.toLowerCase()) ||
          clause.clause_content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply type filter
    if (clauseTypeFilter !== 'all') {
      if (clauseTypeFilter === 'Extra') {
        filtered = filtered.filter((clause) => clause.isExtra);
      } else {
        filtered = filtered.filter((clause) => clause.clause_type === clauseTypeFilter);
      }
    }

    setFilteredClauses(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allClauses, searchQuery, clauseTypeFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredClauses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClauses = filteredClauses.slice(startIndex, endIndex);

  // Load responsible users from CLM
  useEffect(() => {
    const loadUsers = async () => {
      if (!currentContract?.tenantId) return;
      
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/addin/users-public?tenantId=${encodeURIComponent(currentContract.tenantId)}&limit=100`,
          {
            headers: {
              'x-addin-token': API_TOKEN, 
              'ngrok-skip-browser-warning': 'true',
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          const users = data.users || [];
          setResponsibleUsers(users);
          if (users.length > 0) {
            setSelectedUserId(users[0].id);
          }
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };

    loadUsers();
  }, [currentContract?.tenantId]);

  const handleScrollToClause = async (clause: TemplateClause) => {
    if (!clause.isExtra) return;
    if (!isWordApiAvailable()) {
      setError('Word is not available in this context.');
      return;
    }

    try {
      await Word.run(async (context) => {
        const body = context.document.body;
        // Search for the clause content in the document
        const searchText = clause.clause_content.substring(0, 100);
        const searchResults = body.search(searchText, { matchCase: false, matchWholeWord: false });
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length > 0) {
          // Select and scroll to the first match
          searchResults.items[0].select();
          await context.sync();
        } else {
          setError('Clause not found in document');
        }
      });
    } catch (err) {
      console.error('Error scrolling to clause:', err);
      setError('Failed to locate clause in document');
    }
  };

  const [error, setError] = useState<string | null>(null);

  const handleSaveToClm = async () => {
    if (!currentContract) return;

    setSaving(true);
    setSaveStatus(null);

    try {
      // Determine if this is an update or create
      const isUpdate = selectedClause && !selectedClause.isExtra;
      const method = isUpdate ? 'PUT' : 'POST';
      const url = isUpdate 
        ? `${API_BASE_URL}/api/addin/clause-libraries-public/${selectedClause.id}`
        : `${API_BASE_URL}/api/addin/clause-libraries-public`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          clause_head: formData.clauseHead,
          clause_content: formData.clauseContent,
          clause_type: formData.clauseType || null,
          tenant_id: currentContract.tenantId,
          template_ids: [currentContract.templateId],
          responsible_user_id: selectedUserId || null,
        }),
      });

      if (response.ok) {
        const isUpdate = selectedClause && !selectedClause.isExtra;
        setSaveStatus({ 
          success: true, 
          message: isUpdate ? 'Clause updated successfully!' : 'Clause saved to CLM successfully!' 
        });
        setShowSaveForm(false);
        setShowCreateForm(false);
        setSelectedClause(null);
        // Reload library clauses and merge with extra clauses from localStorage
        const reloadResponse = await fetch(
          `${API_BASE_URL}/api/addin/clause-libraries-public?tenantId=${encodeURIComponent(currentContract.tenantId)}&templateId=${encodeURIComponent(currentContract.templateId)}`,
          { headers: { 'ngrok-skip-browser-warning': 'true', 'x-addin-token': API_TOKEN } }
        );
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          const libraryClauses = data.clauseLibraries || [];
          
          // Preserve and merge extra clauses
          const storedExtraClauses = localStorage.getItem('ezylex-extra-clauses');
          if (storedExtraClauses) {
            const extras = JSON.parse(storedExtraClauses);
            setExtraClauses(extras);
            
            const extraAsTemplate = extras.map((ec: ExtraClause) => ({
              id: ec.id,
              clause_head: ec.title,
              clause_content: ec.text,
              clause_type: 'Optional',
              isExtra: true,
            }));
            
            setAllClauses([...libraryClauses, ...extraAsTemplate]);
          } else {
            setAllClauses(libraryClauses);
          }
        }
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        const errorData = await response.json();
        setSaveStatus({ success: false, message: errorData.error || 'Failed to save clause to CLM' });
      }
    } catch (error) {
      console.error('Error saving clause:', error);
      setSaveStatus({ success: false, message: 'An error occurred while saving' });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectClause = (clause: TemplateClause) => {
    setSelectedClause(clause);
    setFormData({
      clauseHead: clause.clause_head,
      clauseContent: clause.clause_content,
      clauseType: (clause.clause_type as any) || 'Optional',
      templates: currentContract ? [currentContract.templateId] : [],
    });
    setShowSaveForm(true);
  };

  const handleCreateNewClause = () => {
    setSelectedClause(null);
    setFormData({
      clauseHead: '',
      clauseContent: '',
      clauseType: 'Optional',
      templates: currentContract ? [currentContract.templateId] : [],
    });
    setShowCreateForm(true);
  };

  return (
    <div className="p-3 h-full bg-gray-50 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-600 rounded-lg">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-gray-900">Clause Library</h1>
              <p className="text-[10px] text-gray-500">
                {extraClauses.length > 0 
                  ? `${extraClauses.length} extra detected`
                  : 'Manage clauses'}
              </p>
            </div>
          </div>
          <button
            onClick={handleCreateNewClause}
            className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-[11px] flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            New Clause
          </button>
        </div>

        {saveStatus && (
          <div
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
              saveStatus.success
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {saveStatus.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{saveStatus.message}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm bg-red-50 text-red-800 border border-red-200">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clauses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setClauseTypeFilter('all')}
              className={`px-2 py-1 text-[10px] rounded-lg transition-colors ${
                clauseTypeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({allClauses.length})
            </button>
            <button
              onClick={() => setClauseTypeFilter('Mandatory')}
              className={`px-2 py-1 text-[10px] rounded-lg transition-colors ${
                clauseTypeFilter === 'Mandatory'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Mandatory
            </button>
            <button
              onClick={() => setClauseTypeFilter('Optional')}
              className={`px-2 py-1 text-[10px] rounded-lg transition-colors ${
                clauseTypeFilter === 'Optional'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Optional
            </button>
            <button
              onClick={() => setClauseTypeFilter('Conditional')}
              className={`px-2 py-1 text-[10px] rounded-lg transition-colors ${
                clauseTypeFilter === 'Conditional'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Conditional
            </button>
            <button
              onClick={() => setClauseTypeFilter('Extra')}
              className={`px-2 py-1 text-[10px] rounded-lg transition-colors ${
                clauseTypeFilter === 'Extra'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Extra ({extraClauses.length})
            </button>
          </div>
        </div>

{loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-3 animate-spin" />
            <p className="text-gray-600">Loading clauses...</p>
          </div>
        ) : filteredClauses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">
              {searchQuery || clauseTypeFilter !== 'all' ? 'No clauses match your filters' : 'No clauses found'}
            </p>
            <p className="text-xs text-gray-500">
              {searchQuery || clauseTypeFilter !== 'all' ? 'Try adjusting your search or filters' : 'Create a new clause or run document analysis to detect clauses'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedClauses.map((clause) => (
              <div
                key={clause.id}
                className={`bg-white rounded-lg shadow-sm border p-3 hover:shadow-md transition-shadow ${
                  clause.isExtra ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 text-sm">{clause.clause_head}</h3>
                      {clause.isExtra && (
                        <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-[10px] rounded-full font-medium">
                          Extra
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                        clause.clause_type === 'Mandatory' ? 'bg-red-100 text-red-700' :
                        clause.clause_type === 'Conditional' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {clause.clause_type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">{clause.clause_content.substring(0, 150)}...</p>
                  </div>
                  <div className="flex gap-2">
                    {clause.isExtra && (
                      <button
                        onClick={() => handleScrollToClause(clause)}
                        className="p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                        title="Scroll to clause in document"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleSelectClause(clause)}
                      className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      title={clause.isExtra ? 'Save to CLM' : 'View Details'}
                    >
                      {clause.isExtra ? <Save className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <div className="text-xs text-gray-600 mb-3 text-center">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredClauses.length)} of {filteredClauses.length} clauses
                </div>
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {(showSaveForm && selectedClause) || showCreateForm ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">
                  {showCreateForm ? 'Create New Clause' : 'Save Clause to CLM'}
                </h2>
                <button
                  onClick={() => {
                    setShowSaveForm(false);
                    setShowCreateForm(false);
                    setSelectedClause(null);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clause Title *
                  </label>
                  <input
                    type="text"
                    value={formData.clauseHead}
                    onChange={(e) => setFormData({ ...formData, clauseHead: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter clause title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clause Content *
                  </label>
                  <textarea
                    value={formData.clauseContent}
                    onChange={(e) => setFormData({ ...formData, clauseContent: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter clause content"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clause Type
                  </label>
                  <select
                    value={formData.clauseType}
                    onChange={(e) => setFormData({ ...formData, clauseType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Type (Optional) --</option>
                    <option value="Mandatory">Mandatory</option>
                    <option value="Optional">Optional</option>
                    <option value="Conditional">Conditional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Responsible User *
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {responsibleUsers.length === 0 ? (
                      <option value="">Loading users...</option>
                    ) : (
                      responsibleUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the user responsible for this clause
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Current template: {currentContract?.templateTypeName || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500">
                    The clause will be saved to the current template by default.
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowSaveForm(false);
                    setShowCreateForm(false);
                    setSelectedClause(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveToClm}
                  disabled={saving || !formData.clauseHead || !formData.clauseContent}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {showCreateForm ? 'Create Clause' : 'Save to CLM'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
